<template>
  <div>
    <b-modal
      id="change-enrollment-grade-modal"
      ref="change-enrollment-grade-modal"
      title="修改課程"
      no-close-on-backdrop
      size="sm"
    >
      <v-select
        v-model="selected_enrollment.grade_id"
        style="width:100%"
        :options="gradeOptions"
        label="label"
        :clearable="false"
        :append-to-body="true"
        class="center-select"
        :reduce="grade => grade.grade_id"
      />

      <template #modal-footer="{ cancel }">
        <b-button size="sm" variant="outline-secondary" @click="cancel()">取消</b-button>
        <b-button size="sm" variant="primary" @click="submit_change_enrollment_grade('')">
          確定
        </b-button>
        <b-button size="sm" variant="warning" @click="submit_change_enrollment_grade('all')">
          確定（全部往後）
        </b-button>
      </template>
    </b-modal>

    <b-modal
      id="change-todo-modal"
      ref="change-todo-modal"
      title="修改畫題"
      no-close-on-backdrop
      size="sm"
    >
      <b-form-input
        v-model="selected_enrollment.todo_content"
        type="text"
      />

      <template #modal-footer="{ cancel }">
        <b-button size="sm" variant="outline-secondary" @click="cancel()">取消</b-button>
        <b-button size="sm" variant="primary" @click="change_todo_content()">確定</b-button>
      </template>
    </b-modal>

    <change-enrollment-modal
      ref="changeEnrollmentModal"
      modal-id="change-date-modal-schedule"
      @changed="get_data"
    />

    <b-overlay :show="loading" rounded="sm">
      <b-card no-body>
        <!-- Filter bar -->
        <div class="p-1">
          <b-row class="align-items-center">
            <b-col cols="12" md="2" class="mb-1 mb-md-0">
              <flat-pickr
                v-model="rangeDate"
                class="form-control"
                :config="{ mode: 'single', dateFormat: 'Y-m-d' }"
              />
            </b-col>

            <b-col cols="12" md="4" class="d-flex align-items-center mb-1 mb-md-0">
              <v-select
                v-model="staff_id"
                class="center-select mr-50 flex-grow-1"
                :options="scheduleStaffOptions"
                label="label"
                :clearable="false"
                :append-to-body="true"
                :reduce="staff => staff.value"
                @input="get_data()"
              />

              <v-select
                v-model="selected_start"
                class="center-select flex-grow-1"
                :options="scheduleStartOptions"
                label="label"
                :clearable="false"
                :append-to-body="true"
                :reduce="start => start.value"
                @input="get_data()"
              />
            </b-col>

            <b-col cols="12" md="5" class="d-flex align-items-center mb-1 mb-md-0">
              <b-badge variant="light-primary" class="mr-1 action-badge" @click="selectAll">
                全選
              </b-badge>

              <template v-if="selectedEnrollmentIds.length">
                <span class="mr-1">已選 {{ selectedEnrollmentIds.length }} 筆</span>

                <b-badge
                  variant="success"
                  class="mr-1 action-badge"
                  @click="callbackSelected"
                >
                  {{ callbackLoading ? '處理中...' : '出席' }}
                </b-badge>

                <b-badge
                  variant="light-warning"
                  class="mr-1 action-badge"
                  @click="clearSelections"
                >
                  清除選擇
                </b-badge>

                <v-select
                  v-model="batchTeacherId"
                  class="teacher-select mr-50"
                  :options="staffOptions"
                  label="label"
                  :clearable="false"
                  :append-to-body="true"
                  :reduce="teacher => teacher.value"
                />

                <b-badge
                  variant="light-primary"
                  class="action-badge"
                  @click="change_enrollment('user_id', batchTeacherId, selectedEnrollmentIds)"
                >
                  轉老師
                </b-badge>
              </template>
            </b-col>

            <b-col cols="12" md="1" class="text-right">
              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                class="btn-icon"
                variant="flat-primary"
                @click="timetable_print()"
              >
                <feather-icon icon="PrinterIcon" />
              </b-button>
            </b-col>
          </b-row>
        </div>

        <div class="vertical-timetable-scroll">
          <div class="vertical-timetable-page">
            <div class="timetable-titlebar">
              <div>
                <div class="timetable-title">{{ displayDate }}</div>
                <div class="timetable-subtitle">
                  日課程時間表（{{ items.length }} 位學生 / {{ teacherGroups.length }} 位老師）
                </div>
              </div>
            </div>

            <div
              v-if="teacherGroups.length"
              class="vertical-schedule"
              :style="scheduleStyle"
            >
              <!-- Top-left -->
              <div class="corner-header">
                時間
              </div>

              <!-- Teacher headers -->
              <div
                v-for="(teacher, teacherIndex) in teacherGroups"
                :key="'head-' + teacher.key"
                class="vertical-teacher-header"
                :class="teacherTheme(teacherIndex).name"
              >
                <strong>{{ teacher.display_name }}</strong>
                <small v-if="teacher.teacher_id">ID: {{ teacher.teacher_id }}</small>
              </div>

              <!-- Left time scale -->
              <div class="vertical-time-axis">
                <div
                  v-for="time in quarterTimes"
                  :key="time.key"
                  class="time-row-label"
                  :class="{
                    'hour-start': time.minute === 0,
                    'half-hour': time.minute === 30
                  }"
                  :style="{ height: quarterHeight + 'px' }"
                >
                  <strong v-if="time.minute === 0">{{ time.label }}</strong>
                  <span v-else>{{ time.minuteLabel }}</span>
                </div>
              </div>

              <!-- Teacher timetable columns -->
              <div
                v-for="(teacher, teacherIndex) in teacherGroups"
                :key="'body-' + teacher.key"
                class="vertical-teacher-column"
                :style="teacherColumnStyle"
              >
                <div
                  v-for="slot in teacher.slots"
                  :key="slot.enrollment_id"
                  class="vertical-slot"
                  :class="[
                    teacherTheme(teacherIndex).slot,
                    slotStatusClasses(slot),
                    { selected: isSelected(slot.enrollment_id) }
                  ]"
                  :style="verticalSlotStyle(slot, teacher)"
                  :title="slotTitle(slot)"
                  @click="toggleSelect(slot.enrollment_id)"
                  @dblclick.stop="$refs.changeEnrollmentModal.open(slot)"
                >
                  <div class="vertical-slot-content">
                    <div class="slot-time">
                      {{ shortTime(slot.start) }} - {{ shortTime(slot.end) }}
                    </div>

                    <div class="slot-mainline">
                      <span class="student-name">
                        {{ studentDisplayName(slot) }}
                      </span>

                      <span
                        v-if="ageShort(slot.birth)"
                        class="student-age"
                      >
                        {{ ageShort(slot.birth) }}
                      </span>
                    </div>

                    <span
                      v-if="slotGradeName(slot)"
                      class="class-grade"
                      :style="gradeStyle(slot)"
                    >
                      {{ slotGradeName(slot) }}
                    </span>

                    <span
                      v-if="!slot._has_valid_time"
                      class="remarks"
                    >
                      未設定時間
                    </span>

                    <span
                      v-if="slot.timetable_remarks"
                      class="remarks"
                    >
                      {{ slot.timetable_remarks }}
                    </span>

                    <span
                      v-if="slot.student_remarks"
                      class="remarks"
                    >
                      {{ slot.student_remarks }}
                    </span>

                    <span
                      v-if="slot.todo_content"
                      class="todo"
                      @dblclick.stop="change_todo(slot)"
                    >
                      {{ slot.todo_content }}
                    </span>

                    <span
                      v-if="slot.change_from > 0 || slot.change_from_class_date"
                      class="change-from-text"
                    >
                      {{ slot.change_from_class_date }}
                    </span>

                    <span
                      v-if="slot.enrollment_status === 'NONPAID'"
                      class="nonpaid"
                    >
                      未繳費
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="no-data">
              當日沒有課堂資料
            </div>

            <div class="timetable-help">
              單擊課堂：選取 / 取消選取　｜　雙擊課堂：轉堂
            </div>
          </div>
        </div>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import flatPickr from 'vue-flatpickr-component'
import moment from 'moment'
import ChangeEnrollmentModal from '../member/member-edit/ChangeEnrollmentModal.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

import {
  BCard,
  BOverlay,
  BBadge,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BModal,
} from 'bootstrap-vue'

export default {
  name: 'DailyScheduleTimetable2',

  components: {
    BCard,
    BOverlay,
    BBadge,
    BRow,
    BCol,
    BFormInput,
      BButton,
    BModal,
      vSelect,
    flatPickr,
    ChangeEnrollmentModal,
    ToastificationContent,
  },

  directives: {
    Ripple,
  },

  data() {
    return {
      rangeDate: moment().format('YYYY-MM-DD'),
      batchTeacherId: null,
      selected_enrollment: {
        class_date: null,
        class_hour: null,
        class_minute: null,
      },

      loading: false,
      selected_start: null,
      center_id: this.$store.getters['center/currentCenterId'],
      staffOptions: [],
      gradeOptions: [],
      staff_id: null,

      items: [],
      selectedEnrollmentIds: [],
      callbackLoading: false,
      scheduleStaffOptions: [],
      scheduleStartOptions: [],

      // 每 15 分鐘高度。想再壓縮可改成 20 / 22。
      quarterHeight: 28,
      teacherColumnWidth: 220,

      themes: [
        { name: 'blue-name', slot: 'blue' },
        { name: 'green-name', slot: 'green' },
        { name: 'orange-name', slot: 'orange' },
        { name: 'purple-name', slot: 'purple' },
        { name: 'cyan-name', slot: 'cyan' },
        { name: 'pink-name', slot: 'pink' },
      ],
    }
  },

  computed: {
    displayDate() {
      return moment(this.rangeDate, 'YYYY-MM-DD').format('YYYY MMM DD ddd')
    },

    validTimedItems() {
      return this.items.filter(item => this.isValidTimeRange(item.start, item.end))
    },

    timelineStartHour() {
      if (!this.validTimedItems.length) return 9

      const earliest = Math.min(
        ...this.validTimedItems.map(item => this.timeToMinutes(item.start)),
      )

      return Math.floor(earliest / 60)
    },

    timelineEndHour() {
      if (!this.validTimedItems.length) return 20

      const latest = Math.max(
        ...this.validTimedItems.map(item => this.timeToMinutes(item.end)),
      )

      const end = Math.ceil(latest / 60)
      return end <= this.timelineStartHour ? this.timelineStartHour + 1 : end
    },

    timelineGridColumns() {
      return (this.timelineEndHour - this.timelineStartHour) * 4
    },

    bodyHeight() {
      return this.timelineGridColumns * this.quarterHeight
    },

    quarterTimes() {
      const result = []
      const start = this.timelineStartHour * 60
      const end = this.timelineEndHour * 60

      for (let min = start; min < end; min += 15) {
        const hour = Math.floor(min / 60)
        const minute = min % 60

        result.push({
          key: min,
          hour,
          minute,
          label: `${String(hour).padStart(2, '0')}:00`,
          minuteLabel: String(minute).padStart(2, '0'),
        })
      }

      return result
    },

    scheduleStyle() {
      return {
        // 左邊 82px 為時間軸，其餘空間由所有老師平均分配
        // 1 位老師 = 100%，2 位 = 各 50%，3 位 = 各 33.33%...
        gridTemplateColumns: `82px repeat(${this.teacherGroups.length}, minmax(0, 1fr))`,
      }
    },

    teacherColumnStyle() {
      return {
        height: `${this.bodyHeight}px`,
        '--quarter-height': `${this.quarterHeight}px`,
        '--hour-height': `${this.quarterHeight * 4}px`,
        '--two-hour-height': `${this.quarterHeight * 8}px`,
        '--first-hour-bg': this.timelineStartHour % 2 === 1 ? '#efefef' : 'transparent',
        '--second-hour-bg': this.timelineStartHour % 2 === 1 ? 'transparent' : '#efefef',
      }
    },

    teacherGroups() {
      const groups = {}

      const sortedItems = [...this.items].sort((a, b) => {
        const teacherA = this.teacherName(a.user_id, a)
        const teacherB = this.teacherName(b.user_id, b)

        if (teacherA !== teacherB) {
          return teacherA.localeCompare(teacherB, 'zh-Hant')
        }

        return this.timeToMinutes(a.start) - this.timeToMinutes(b.start)
      })

      sortedItems.forEach(item => {
        const teacherId = item.user_id || item.teacher_id || null
        const key = teacherId || 'NO_TEACHER'

        if (!groups[key]) {
          groups[key] = {
            key,
            teacher_id: teacherId,
            display_name: this.teacherName(teacherId, item),
            rows: [],
            slots: [],
          }
        }

        const hasValidTime = this.isValidTimeRange(item.start, item.end)
        const startMin = hasValidTime
          ? this.timeToMinutes(item.start)
          : this.timelineStartHour * 60

        const endMin = hasValidTime
          ? this.timeToMinutes(item.end)
          : startMin + 15

        let rowNo = 1

        if (!hasValidTime) {
          rowNo = groups[key].rows.length + 1
          groups[key].rows[rowNo - 1] = []
        } else {
          while (true) {
            const rowIndex = rowNo - 1

            if (!groups[key].rows[rowIndex]) {
              groups[key].rows[rowIndex] = []
              break
            }

            const conflict = groups[key].rows[rowIndex].some(existing => (
              startMin < existing._end_min
              && endMin > existing._start_min
            ))

            if (!conflict) break
            rowNo += 1
          }
        }

        const slot = {
          ...item,
          _start_min: startMin,
          _end_min: endMin,
          _has_valid_time: hasValidTime,
          _row_no: rowNo,
        }

        groups[key].rows[rowNo - 1].push(slot)
        groups[key].slots.push(slot)
      })

      return Object.values(groups)
    },
  },

  watch: {
    rangeDate() {
      this.clearSelections()
      this.get_data()
    },
  },

  created() {
    this.get_data()
    this.get_staff_list()
    this.get_grade()
  },

  methods: {
    timeToMinutes(time) {
      if (!time || typeof time !== 'string') return 0

      const match = time.match(/^([01]\d|2[0-3]):([0-5]\d)/)
      if (!match) return 0

      return (parseInt(match[1], 10) * 60) + parseInt(match[2], 10)
    },

    isValidTimeRange(start, end) {
      if (!start || !end) return false

      const validStart = /^([01]\d|2[0-3]):[0-5]\d/.test(String(start))
      const validEnd = /^([01]\d|2[0-3]):[0-5]\d/.test(String(end))

      return validStart
        && validEnd
        && this.timeToMinutes(end) > this.timeToMinutes(start)
    },

    verticalSlotStyle(slot, teacher) {
      const scheduleStart = this.timelineStartHour * 60
      const laneCount = Math.max(1, teacher.rows.length)
      const laneGap = 4

      let topMin = slot._has_valid_time
        ? slot._start_min
        : scheduleStart

      let duration = slot._has_valid_time
        ? slot._end_min - slot._start_min
        : 15

      topMin = Math.max(scheduleStart, topMin)
      duration = Math.max(15, duration)

      const top = ((topMin - scheduleStart) / 15) * this.quarterHeight
      const height = Math.max(
        this.quarterHeight - 4,
        (duration / 15) * this.quarterHeight - 4,
      )

      const widthPercent = 100 / laneCount
      const leftPercent = (slot._row_no - 1) * widthPercent

      return {
        top: `${top + 2}px`,
        height: `${height}px`,
        left: `calc(${leftPercent}% + ${laneGap / 2}px)`,
        width: `calc(${widthPercent}% - ${laneGap}px)`,
      }
    },

    teacherTheme(index) {
      return this.themes[index % this.themes.length]
    },

    teacherName(teacherId, item = {}) {
      if (item.display_name) return item.display_name
      if (item.teacher_name) return item.teacher_name
      if (item.user_name) return item.user_name

      const option = this.scheduleStaffOptions.find(
        row => String(row.value) === String(teacherId),
      ) || this.staffOptions.find(
        row => String(row.value) === String(teacherId),
      )

      return option ? option.label : (teacherId ? `Teacher #${teacherId}` : '未有老師')
    },

    studentDisplayName(slot) {
      return slot.student_name
        || slot.zh_name
        || slot.student_zh_name
        || `Student #${slot.student_id}`
    },

    slotGradeName(slot) {
      return slot.grade
        || slot.grade_name
        || slot.class_grade
        || ''
    },

    gradeStyle(slot) {
      let color = slot.grade_color || slot.color || ''

      if (!color) return {}

      color = String(color).trim()

      if (!color.startsWith('#') && !color.startsWith('rgb')) {
        color = `#${color}`
      }

      return {
        backgroundColor: color,
      }
    },

    slotStatusClasses(slot) {
      return {
        leave: slot.enrollment_status === 'LEAVE',
        trial: slot.enrollment_type === 'TRIAL',
        'short-course': slot.enrollment_type === 'SHORT_COURSE',
        changed: Number(slot.change_from) > 0 || !!slot.change_from_class_date,
      }
    },

    slotTitle(slot) {
      return [
        this.studentDisplayName(slot),
        `${slot.start || '--:--'} - ${slot.end || '--:--'}`,
        slot.class_id ? `Class #${slot.class_id}` : '',
      ].filter(Boolean).join('\n')
    },

    ageShort(dob) {
      if (!dob || !moment(dob, 'YYYY-MM-DD', true).isValid()) return ''

      const birth = moment(dob, 'YYYY-MM-DD')
      const ref = moment(this.rangeDate, 'YYYY-MM-DD')

      if (ref.isBefore(birth)) return ''

      const years = ref.diff(birth, 'years')
      const afterYears = birth.clone().add(years, 'years')
      const months = ref.diff(afterYears, 'months')

      return `${years}.${months}yr`
    },

    shortTime(time) {
      return time ? String(time).substr(0, 5) : '--:--'
    },

    isSelected(enrollmentId) {
      return this.selectedEnrollmentIds.includes(enrollmentId)
    },

    get_grade() {
      this.$http.get(`${process.env.VUE_APP_API_BASE}/grades/`)
        .then(res => {
          this.gradeOptions = res.data.grades_enable
        })
    },

    submit_change_enrollment_grade(action) {
      this.$http.post(
        `${process.env.VUE_APP_API_BASE}/enrollment/${this.selected_enrollment.enrollment_id}/change_grade/?action=${action}`,
        {
          grade_id: this.selected_enrollment.grade_id,
          action,
        },
      )
        .then(() => {
          this.$refs['change-enrollment-grade-modal'].hide()
          this.successToast('調整成功')
          this.get_data()
        })
    },

    change_leave(enrollment) {
      const action = enrollment.enrollment_status === 'LEAVE'
        ? 'rollback'
        : 'absent'

      this.$http.post(
        `${process.env.VUE_APP_API_BASE}/enrollment/${enrollment.enrollment_id}/absent/?action=${action}`,
        {},
      )
        .then(() => {
          this.successToast('請假狀態已更新')
          this.get_data()
        })
    },

    change_todo(enrollment) {
      this.selected_enrollment = enrollment
      this.$refs['change-todo-modal'].show()
    },

    change_enrollment_grade(enrollment) {
      this.selected_enrollment = enrollment
      this.$refs['change-enrollment-grade-modal'].show()
    },

    change_todo_content() {
      this.$http.post(
        `${process.env.VUE_APP_API_BASE}/enrollment/${this.selected_enrollment.enrollment_id}/update_todo/`,
        {
          todo_content: this.selected_enrollment.todo_content,
        },
      )
        .then(() => {
          this.successToast('修改畫題成功')
          this.$refs['change-todo-modal'].hide()
          this.get_data()
        })
    },

    timetable_print() {
      const date = moment(this.rangeDate).format('YYYY-MM-DD')
      const userData = JSON.parse(localStorage.getItem('userData') || '{}')

      window.open(
        `//rb.cloudedu.pro/timetable-v3.php?token=${userData.token || ''}&date=${date}&center_id=${this.$store.getters['center/currentCenterId']}`,
        '_blank',
      )
    },

    toggleSelect(enrollmentId) {
      const idx = this.selectedEnrollmentIds.indexOf(enrollmentId)

      if (idx === -1) {
        this.selectedEnrollmentIds.push(enrollmentId)
      } else {
        this.selectedEnrollmentIds.splice(idx, 1)
      }
    },

    clearSelections() {
      this.selectedEnrollmentIds = []
    },

    selectAll() {
      this.selectedEnrollmentIds = this.items.map(item => item.enrollment_id)
    },

    callbackSelected() {
      if (!this.selectedEnrollmentIds.length || this.callbackLoading) return

      this.callbackLoading = true

      this.$http.post(
        `${process.env.VUE_APP_API_BASE}/enrollment/batch/change_enrollment/`,
        {
          enrollment_ids: this.selectedEnrollmentIds,
          field: 'is_attend',
          value: 'Y',
        },
      )
        .then(() => {
          this.successToast('操作成功')
          this.selectedEnrollmentIds = []
          this.get_data()
        })
        .catch(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: '操作失敗',
              icon: 'XIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          this.callbackLoading = false
        })
    },

    change_enrollment(field, value, enrollmentIds) {
      this.$http.post(
        `${process.env.VUE_APP_API_BASE}/enrollment/batch/change_enrollment/`,
        {
          field,
          value,
          enrollment_ids: enrollmentIds,
        },
      )
        .then(() => {
          this.get_data()
        })
    },

    get_staff_list() {
      this.$http.get(
        `${process.env.VUE_APP_API_BASE}/staffs/select/?center_id=${this.center_id}`,
      )
        .then(res => {
          this.staffOptions = res.data
        })
    },

    get_data() {
      this.loading = true

      this.$http.get(
        `${process.env.VUE_APP_API_BASE}/report/daily-schedule/?start=${this.selected_start}&center=${this.center_id}&date=${this.rangeDate}&staff_id=${this.staff_id}`,
      )
        .then(res => {
          this.items = res.data.enrollments || []
          this.scheduleStaffOptions = res.data.staff || []
          this.scheduleStartOptions = res.data.start || []
        })
        .finally(() => {
          this.loading = false
        })
    },

    successToast(title) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: 'CheckIcon',
          variant: 'success',
        },
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';

.teacher-select {
  min-width: 150px;
}

body .vs__dropdown-menu {
  z-index: 9999 !important;
}

body .vs__dropdown-toggle {
  background-color: #fff;
}

.action-badge {
  cursor: pointer;
  user-select: none;
}

.vertical-timetable-scroll {
  width: 100%;
  overflow: auto;
  border-top: 1px solid #ebe9f1;
}

.vertical-timetable-page {
  min-width: 900px;
  padding: 18px 20px 24px;
  color: #333;
  background: #fff;
}

.timetable-titlebar {
  margin-bottom: 12px;
}

.timetable-title {
  font-size: 25px;
  font-weight: 700;
  letter-spacing: 1px;
}

.timetable-subtitle {
  margin-top: 4px;
  color: #777;
  font-size: 12px;
}

.vertical-schedule {
  display: grid;
  grid-template-rows: 68px auto;
  width: 100%;
  min-width: 100%;
  border-top: 2px solid #777;
  border-left: 1px solid #dfe3e7;
}

.corner-header {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 9;
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid #cfd4d8;
  border-bottom: 1px solid #cfd4d8;
  background: #fff;
  font-weight: 700;
}

.vertical-teacher-header {
  position: sticky;
  top: 0;
  z-index: 8;
  display: flex;
  min-width: 0;
  padding: 8px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-right: 1px solid #cfd4d8;
  border-bottom: 1px solid #cfd4d8;
  text-align: center;
}

.vertical-teacher-header strong {
  font-size: 17px;
}

.vertical-teacher-header small {
  margin-top: 4px;
  font-size: 10px;
}

.vertical-time-axis {
  grid-column: 1;
  grid-row: 2;
  position: sticky;
  left: 0;
  z-index: 7;
  background: #fff;
  border-right: 1px solid #cfd4d8;
}

.time-row-label {
  display: flex;
  padding-right: 8px;
  align-items: flex-start;
  justify-content: flex-end;
  border-bottom: 1px solid #e5e5e5;
  color: #777;
  font-size: 10px;
  line-height: 1;
}

.time-row-label.hour-start {
  border-bottom-color: #c6c6c6;
}

.time-row-label strong {
  transform: translateY(-5px);
  color: #444;
  font-size: 12px;
}

.time-row-label span {
  transform: translateY(-4px);
}

.vertical-teacher-column {
  position: relative;
  grid-row: 2;
  min-width: 0;
  border-right: 1px solid #cfd4d8;

  background:
    repeating-linear-gradient(
      to bottom,
      transparent 0,
      transparent calc(var(--quarter-height) - 1px),
      #ddd calc(var(--quarter-height) - 1px),
      #ddd var(--quarter-height)
    ),
    repeating-linear-gradient(
      to bottom,
      var(--first-hour-bg) 0,
      var(--first-hour-bg) var(--hour-height),
      var(--second-hour-bg) var(--hour-height),
      var(--second-hour-bg) var(--two-hour-height)
    );
}

.vertical-slot {
  position: absolute;
  z-index: 2;
  display: flex;
  min-width: 0;
  padding: 4px 5px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px solid;
  border-radius: 5px;
  font-size: 11px;
  line-height: 1.2;
  text-align: center;
  cursor: pointer;
  transition: box-shadow .15s ease, transform .15s ease;
}

.vertical-slot:hover {
  z-index: 5;
  overflow: visible;
  box-shadow: 0 4px 12px rgba(0, 0, 0, .16);
}

.vertical-slot.selected {
  box-shadow: inset 0 0 0 3px #7367f0;
}

.vertical-slot-content {
  width: 100%;
  min-width: 0;
}

.slot-time {
  margin-bottom: 2px;
  opacity: .72;
  font-size: 9px;
  font-weight: 600;
}

.slot-mainline {
  line-height: 1.35;
}

.student-name {
  font-weight: 700;
}

.student-age {
  margin-left: 3px;
  font-size: 9px;
  font-weight: 600;
}

.class-grade {
  display: inline-block;
  margin-top: 2px;
  padding: 1px 7px;
  border-radius: 4px;
  background: #000;
  color: #444;
  font-size: 9px;
  font-weight: 700;
}

.vertical-slot.blue,
.vertical-slot.green,
.vertical-slot.orange,
.vertical-slot.purple,
.vertical-slot.cyan,
.vertical-slot.pink {
  background: #fef5ee;
}

.vertical-slot.blue {
  border-color: #bed5e8;
}

.vertical-slot.green {
  border-color: #cfdfbd;
}

.vertical-slot.orange {
  border-color: #efd4b8;
}

.vertical-slot.purple {
  border-color: #d8c8e9;
}

.vertical-slot.cyan {
  border-color: #c4e4e5;
}

.vertical-slot.pink {
  border-color: #ecc9d6;
}

.vertical-slot.trial {
  background: #fdf5a0;
  border: 2px solid #ecad32;
}

.vertical-slot.short-course {
  background: #fef5ee;
  border: 2px solid #c8a3f2;
}

.vertical-slot.leave {
  color: red;
  font-weight: 700;
}

.vertical-slot.changed {
  box-shadow: inset 0 0 0 2px #198754;
}

.vertical-slot.changed.selected {
  box-shadow:
    inset 0 0 0 2px #198754,
    inset 0 0 0 5px #7367f0;
}

.remarks {
  display: block;
  margin-top: 2px;
  color: #b02a37;
  font-size: 9px;
}

.todo {
  display: block;
  margin-top: 2px;
  color: #666;
  font-size: 9px;
}

.vertical-slot.leave .todo {
  color: red;
  font-weight: 700;
}

.change-from-text {
  display: block;
  margin-top: 2px;
  color: #198754;
  font-size: 9px;
  font-weight: 700;
}

.nonpaid {
  display: block;
  margin-top: 2px;
  color: #ea5455;
  font-size: 9px;
  font-weight: 700;
}

.no-data {
  padding: 50px;
  color: #999;
  font-size: 20px;
  text-align: center;
}

.timetable-help {
  margin-top: 10px;
  color: #999;
  font-size: 11px;
}

.blue-name {
  color: #2d6fa6;
  background: #f1f8fd;
}

.green-name {
  color: #4b8c3f;
  background: #f3f9ee;
}

.orange-name {
  color: #d36b1e;
  background: #fff7ef;
}

.purple-name {
  color: #6536a4;
  background: #faf5ff;
}

.cyan-name {
  color: #2894a0;
  background: #effbfc;
}

.pink-name {
  color: #c72c63;
  background: #fff3f7;
}

@media print {
  .vertical-timetable-scroll {
    overflow: visible;
  }

  .vertical-timetable-page {
    min-width: 0;
    padding: 0;
  }

  .corner-header,
  .vertical-teacher-header,
  .vertical-time-axis {
    position: static;
  }

  @page {
    size: A3 portrait;
    margin: 8mm;
  }
}
</style>
